import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControlLabel, IconButton, Link, MenuItem, Stack, Tab, Tabs, TextField, Tooltip, Typography,
} from "@mui/material";
import { ArrowLeft, Bot, CheckCircle2, GitMerge, Lock, Mail, MessageCircle, Phone, RotateCcw, Sparkles, Star, Trash2, UserRound, XCircle } from "lucide-react";
import { HelpdeskPage, useHd } from "./HelpdeskContext";
import { hd, errorMessage, errorDetails } from "./api";
import { getAgentSocket, useRoom, useSocketEvent } from "./socket";
import MessageThread, { upsertMessage } from "./MessageThread";
import Composer from "./Composer";
import LinkedIssues from "./LinkedIssues";
import { CustomFieldInputs } from "./CustomFieldInputs";
import { ChannelIcon, PriorityBadge, SentimentChip, SlaBadge, SmallLabel, StatusBadge } from "./ui";
import { GlassCard, PersonAvatar, T, TYPE, formatDateTime, formatRelative, ghostButtonSx, primaryButtonSx } from "../user/complaints/shared";

const Panel = ({ title, icon: Icon, children, action }) => (
  <GlassCard sx={{ p: 2, mb: 2 }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        {Icon && <Icon size={15} color={T.brand} />}
        <Typography sx={{ fontSize: TYPE.small, fontWeight: 700, color: T.ink }}>{title}</Typography>
      </Stack>
      {action}
    </Stack>
    {children}
  </GlassCard>
);

function Detail() {
  const { id } = useParams();
  const ctx = useHd();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [error, setError] = useState("");
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    try {
      const [ticket, m] = await Promise.all([hd.ticket(id), hd.messages(id, { limit: 50 })]);
      setT(ticket);
      setMessages(m.rows);
      setHasMore(m.hasMore);
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Ticket not found"));
    }
  }, [id]);
  const refreshTicket = useCallback(async () => {
    try {
      setT(await hd.ticket(id));
    } catch {
      /* keep current */
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useRoom("ticket", id);
  // collision detection: other agents with this ticket open / writing a reply
  const [viewers, setViewers] = useState([]);
  useSocketEvent("ticket.viewers", (p) => {
    if (String(p?.ticketId) === String(id)) setViewers(p.viewers || []);
  });
  useEffect(() => setViewers([]), [id]);
  const signalReplying = useCallback((on) => getAgentSocket()?.emit("ticket.replying", { ticketId: id, replying: Boolean(on) }), [id]);
  useSocketEvent(["message.created", "message.updated"], (m) => {
    if (String(m.ticketId) !== String(id)) return;
    setMessages((list) => upsertMessage(list, m));
  });
  useSocketEvent(["ticket.updated", "ticket.assigned"], (p) => {
    if (String(p._id) === String(id)) refreshTicket();
  });
  // catch up after reconnect
  useSocketEvent("connect", async () => {
    const last = messages[messages.length - 1];
    if (!last) return;
    const r = await hd.messages(id, { after: last._id });
    setMessages((list) => r.rows.reduce(upsertMessage, list));
  });

  const loadOlder = async () => {
    setLoadingOlder(true);
    try {
      const r = await hd.messages(id, { before: messages[0]?._id, limit: 50 });
      setMessages((list) => [...r.rows, ...list]);
      setHasMore(r.hasMore);
    } finally {
      setLoadingOlder(false);
    }
  };

  const act = async (fn, okMsg) => {
    try {
      const updated = await fn();
      if (updated?._id) setT((prev) => ({ ...prev, ...updated }));
      if (okMsg) ctx.notify(okMsg);
      refreshTicket();
      return true;
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
      return false;
    }
  };

  const activeChat = useMemo(() => (t?.conversations || []).find((c) => c.channel === "chat" && c.status !== "ended"), [t]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!t) return <Box sx={{ py: 10, textAlign: "center" }}><CircularProgress size={26} /></Box>;

  const closed = ["resolved", "closed"].includes(t.statusType);
  const onWhatsApp = !activeChat && t.channel === "whatsapp" && Boolean(t.requester?.phone);
  const send = async ({ content, html, cc, bcc, attachmentIds, internal, statusId }) => {
    const m = internal
      ? await hd.note(id, { content, attachmentIds })
      : await hd.reply(id, { content, html, cc, bcc, attachmentIds, statusId, conversationId: activeChat?._id, channel: onWhatsApp ? "whatsapp" : undefined });
    setMessages((list) => upsertMessage(list, m));
    refreshTicket();
  };
  const runMacro = async (cannedId) => {
    const r = await hd.applyMacro(id, cannedId);
    ctx.notify(r.message);
    refreshTicket();
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: { xs: "auto", lg: "calc(100vh - 120px)" },
        minHeight: 0,
        overflow: { xs: "visible", lg: "hidden" },
      }}
    >
      <Box sx={{ flexShrink: 0, mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <IconButton size="small" onClick={() => navigate(-1)}><ArrowLeft size={18} /></IconButton>
          <Link component={RouterLink} to="/helpdesk/tickets" underline="hover" sx={{ fontSize: TYPE.small }}>Helpdesk</Link>
          <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>/ {t.key}</Typography>
        </Stack>

        <GlassCard sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5} alignItems={{ md: "center" }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap>
                <ChannelIcon channel={t.channel} size={16} />
                <Typography sx={{ fontSize: TYPE.small, fontWeight: 700, color: T.brand }}>Ticket #{t.key}</Typography>
                {t.escalated && <Chip size="small" label={`Escalated L${t.sla?.escalationLevel || 1}`} sx={{ height: 20, bgcolor: "#FEF0C7", color: "#B54708", fontWeight: 700 }} />}
                {t.handledBy === "ai" && <Chip size="small" icon={<Bot size={12} />} label="AI handled" sx={{ height: 20, bgcolor: "#F4EBFF", color: "#5925DC" }} />}
                {t.mergedInto && <Chip size="small" label={`Merged into ${t.mergedInto.key}`} onClick={() => navigate(`/helpdesk/tickets/${t.mergedIntoId}`)} />}
              </Stack>
              <Typography sx={{ fontSize: { xs: "1.1rem", sm: "1.2rem" }, fontWeight: 700, color: T.ink, wordBreak: "break-word" }}>{t.subject}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap alignItems="center">
                <StatusBadge status={t.status} />
                <PriorityBadge priority={t.priority} />
                <SlaBadge ticket={t} />
                <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>Created {formatDateTime(t.createdAt)} · updated {formatRelative(t.updatedAt)}</Typography>
              </Stack>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ flexShrink: 0 }}>
              {activeChat ? (
                <Button size="small" variant="outlined" sx={ghostButtonSx} startIcon={<MessageCircle size={14} />} onClick={() => navigate(`/helpdesk/chat?c=${activeChat._id}`)}>Open conversation</Button>
              ) : (
                <Tooltip title="Live chat starts when the customer opens the chat widget; their chat is attached to this ticket automatically.">
                  <span><Button size="small" variant="outlined" sx={ghostButtonSx} startIcon={<MessageCircle size={14} />} disabled>No active chat</Button></span>
                </Tooltip>
              )}
              {t.requester?.email && <Button size="small" variant="outlined" sx={ghostButtonSx} startIcon={<Mail size={14} />} href={`#composer`}>Send email</Button>}
              {t.requester?.phone && <Button size="small" variant="outlined" sx={ghostButtonSx} startIcon={<Phone size={14} />} href={`tel:${t.requester.phone}`}>Call customer</Button>}
              {!closed && ctx.can("tickets.resolve") && <Button size="small" variant="contained" sx={{ ...primaryButtonSx, bgcolor: "#079455", "&:hover": { bgcolor: "#067647" } }} startIcon={<CheckCircle2 size={14} />} onClick={() => setDialog("resolve")}>Resolve</Button>}
              {t.statusType !== "closed" && ctx.can("tickets.close") && <Button size="small" variant="outlined" sx={ghostButtonSx} startIcon={<XCircle size={14} />} onClick={() => act(() => hd.close(id, {}), `${t.key} closed`)}>Close</Button>}
              {closed && ctx.can("tickets.reopen") && <Button size="small" variant="outlined" sx={ghostButtonSx} startIcon={<RotateCcw size={14} />} onClick={() => setDialog("reopen")}>Reopen</Button>}
              {ctx.can("tickets.merge") && !t.mergedIntoId && <Tooltip title="Merge into another ticket"><IconButton size="small" onClick={() => setDialog("merge")}><GitMerge size={16} /></IconButton></Tooltip>}
              {ctx.can("tickets.delete") && <Tooltip title="Delete ticket"><IconButton size="small" onClick={() => setDialog("delete")}><Trash2 size={16} color="#B42318" /></IconButton></Tooltip>}
            </Stack>
          </Stack>
        </GlassCard>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 350px" },
          gap: 2,
          flex: 1,
          minHeight: 0,
          overflow: { xs: "visible", lg: "hidden" },
        }}
      >
        <GlassCard
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            minHeight: { xs: 520, lg: 0 },
            overflow: "hidden",
          }}
        >
          {t.description && (
            <Box sx={{ px: 2.5, py: 1.25, borderBottom: `1px solid ${T.border}`, bgcolor: T.surfaceMuted, flexShrink: 0 }}>
              <SmallLabel sx={{ mb: 0.25 }}>Description</SmallLabel>
              <Typography sx={{ fontSize: TYPE.small, whiteSpace: "pre-wrap", color: T.inkSecondary, maxHeight: 90, overflowY: "auto" }}>{t.description}</Typography>
            </Box>
          )}
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <MessageThread messages={messages} hasMore={hasMore} onLoadOlder={loadOlder} loadingOlder={loadingOlder} />
          </Box>
          <Box id="composer" sx={{ flexShrink: 0, borderTop: `1px solid ${T.border}` }}>
            <CollisionBar viewers={viewers.filter((v) => String(v.id) !== String(ctx.me.id))} />
            {t.mergedIntoId ? (
              <Alert severity="info" sx={{ m: 2 }}>This ticket was merged. Reply on {t.mergedInto?.key}.</Alert>
            ) : (
              <Composer
                ticketId={id} onSend={send} onMacro={runMacro} onTyping={signalReplying} emailMode={!activeChat && !onWhatsApp && Boolean(t.requester?.email)}
                vars={{ customer_name: t.requester?.name || "", ticket_id: t.key, agent_name: ctx.me.name, ticket_subject: t.subject }}
                placeholder={activeChat ? "Reply in the live chat…" : onWhatsApp ? `Reply on WhatsApp to ${t.requester.phone}…` : t.requester?.email ? `Reply by email to ${t.requester.email}…` : "Reply…"}
              />
            )}
          </Box>
        </GlassCard>

        <Box
          sx={{
            height: "100%",
            overflowY: { xs: "visible", lg: "auto" },
            pr: { xs: 0, lg: 0.5 },
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": { bgcolor: "#CBD5E1", borderRadius: 3 },
            "&::-webkit-scrollbar-thumb:hover": { bgcolor: "#94A3B8" },
          }}
        >
          <Properties t={t} onSaved={refreshTicket} />
          <CustomerPanel t={t} />
          <LinkedIssues ticket={t} />
          <AiPanel t={t} onChanged={refreshTicket} />
          <SlaPanel t={t} />
          <TasksPanel t={t} onChanged={refreshTicket} />
          {(t.csat?.rating || t.csatRequest) && (
            <Panel title="Customer satisfaction" icon={Star}>
              {t.csat?.rating ? (
                <>
                  <Typography sx={{ fontSize: "1.2rem", color: "#F79009" }}>{"★".repeat(t.csat.rating)}{"☆".repeat(5 - t.csat.rating)}</Typography>
                  {t.csat.feedback && <Typography sx={{ fontSize: TYPE.small, mt: 0.5, color: T.inkSecondary }}>"{t.csat.feedback}"</Typography>}
                </>
              ) : (
                <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>Survey sent {formatRelative(t.csatRequest.createdAt)} — no response yet</Typography>
              )}
            </Panel>
          )}
          <HistoryPanel id={id} />
        </Box>
      </Box>

      {dialog === "resolve" && <ResolveDialog t={t} onClose={() => setDialog(null)} onDone={async (body) => (await act(() => hd.resolve(id, body), `${t.key} resolved`)) && setDialog(null)} required={ctx.settings?.tickets?.resolutionNoteRequired} />}
      {dialog === "reopen" && <ReasonDialog title={`Reopen ${t.key}`} label="Reason" onClose={() => setDialog(null)} onDone={async (reason) => (await act(() => hd.reopen(id, { reason }), `${t.key} reopened`)) && setDialog(null)} />}
      {dialog === "merge" && <ReasonDialog title={`Merge ${t.key} into…`} label="Target ticket number (e.g. JW-10291)" onClose={() => setDialog(null)} onDone={async (targetKey) => (await act(async () => { const r = await hd.merge(id, { targetKey }); navigate(`/helpdesk/tickets/${r._id}`); return null; }, "Tickets merged")) && setDialog(null)} />}
      {dialog === "delete" && (
        <Dialog open onClose={() => setDialog(null)}>
          <DialogTitle>Delete {t.key}?</DialogTitle>
          <DialogContent><Typography sx={{ fontSize: TYPE.small }}>The ticket is hidden from every view. Its history is kept in the audit log.</Typography></DialogContent>
          <DialogActions>
            <Button onClick={() => setDialog(null)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={async () => { try { await hd.deleteTicket(id); ctx.notify(`${t.key} deleted`); navigate("/helpdesk/tickets"); } catch (err) { ctx.notify(errorMessage(err), "error"); } }}>Delete</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
function Properties({ t, onSaved }) {
  const ctx = useHd();
  const [saving, setSaving] = useState(false);
  const [cf, setCf] = useState(t.customFields || {});
  const [cfErrors, setCfErrors] = useState({});
  useEffect(() => setCf(t.customFields || {}), [t.customFields]);
  const editable = ctx.can("tickets.update");
  const save = async (fn) => {
    setSaving(true);
    try {
      await fn();
      onSaved();
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
      setCfErrors(errorDetails(err) || {});
    } finally {
      setSaving(false);
    }
  };
  const cats = ctx.categories.filter((c) => c.level === "category" && c.isActive);
  const childrenOf = (id) => (id ? ctx.categories.filter((c) => c.parentId && String(c.parentId) === String(id) && c.isActive) : []);
  const subs = childrenOf(t.categoryId);
  const issues = childrenOf(t.subcategoryId);
  const ticketFields = ctx.customFields.filter((f) => f.entity === "ticket");
  const statuses = ctx.statuses.filter((s) => s.isActive);

  const Select = ({ label, value, options, onChange, disabled, empty = "None" }) => (
    <TextField select size="small" fullWidth label={label} value={value ? String(value) : ""} disabled={disabled || saving} onChange={(e) => onChange(e.target.value || null)} sx={{ mb: 1.5 }}>
      {empty && <MenuItem value="">{empty}</MenuItem>}
      {options.map(([v, l]) => <MenuItem key={v} value={String(v)}>{l}</MenuItem>)}
    </TextField>
  );

  return (
    <Panel title="Properties" action={saving && <CircularProgress size={14} />}>
      <Select label="Status" value={t.statusId} empty={null} options={statuses.map((s) => [s._id, s.name])}
        disabled={!editable}
        onChange={(v) => {
          const target = ctx.statusById.get(String(v));
          if (target?.type === "resolved") return ctx.notify("Use the Resolve button to add a resolution note", "info");
          return save(() => hd.setStatus(t._id, { statusId: v }));
        }} />
      <Select label="Priority" value={t.priorityId} empty={null} options={ctx.priorities.filter((p) => p.isActive).map((p) => [p._id, p.name])} disabled={!editable} onChange={(v) => save(() => hd.updateTicket(t._id, { priorityId: v }))} />
      <Select label="Category" value={t.categoryId} options={cats.map((c) => [c._id, c.name])} disabled={!editable} onChange={(v) => save(() => hd.updateTicket(t._id, { categoryId: v }))} />
      {subs.length > 0 && <Select label="Subcategory" value={t.subcategoryId} options={subs.map((c) => [c._id, c.name])} disabled={!editable} onChange={(v) => save(() => hd.updateTicket(t._id, { subcategoryId: v }))} />}
      {issues.length > 0 && <Select label="Issue type" value={t.issueTypeId} options={issues.map((c) => [c._id, c.name])} disabled={!editable} onChange={(v) => save(() => hd.updateTicket(t._id, { issueTypeId: v }))} />}
      <Select label="Team" value={t.teamId} options={ctx.teams.filter((x) => x.isActive).map((x) => [x._id, x.name])} disabled={!ctx.can("tickets.assign")} onChange={(v) => save(() => hd.assign(t._id, { teamId: v }))} />
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ flexGrow: 1 }}>
          <Select label="Agent" value={t.assignee?.id} empty="Unassigned" options={ctx.agents.map((a) => [a.memberId, `${a.name}${a.presence === "online" ? " • online" : ""}`])} disabled={!ctx.can("tickets.assign")} onChange={(v) => save(() => hd.assign(t._id, { assigneeId: v }))} />
        </Box>
        {String(t.assignee?.id) !== String(ctx.me.id) && <Button size="small" sx={{ mt: 0.5, textTransform: "none" }} onClick={() => save(() => hd.assign(t._id, { assigneeId: ctx.me.id }))}>Take it</Button>}
      </Stack>
      <Autocomplete
        multiple freeSolo size="small" options={ctx.tags} value={t.tags || []} disabled={!editable || saving}
        onChange={(_, v) => save(() => hd.updateTicket(t._id, { tags: v }))}
        renderTags={(v, getTagProps) => v.map((o, i) => <Chip size="small" label={o} {...getTagProps({ index: i })} key={o} />)}
        renderInput={(p) => <TextField {...p} label="Tags" placeholder="Add tag" />}
      />
      {ticketFields.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <SmallLabel sx={{ mb: 1 }}>Custom fields</SmallLabel>
          <CustomFieldInputs fields={ticketFields} values={cf} errors={cfErrors} disabled={!editable} onChange={setCf} />
          {editable && <Button size="small" sx={{ mt: 1 }} onClick={() => save(() => hd.updateTicket(t._id, { customFields: cf }))}>Save fields</Button>}
        </Box>
      )}
    </Panel>
  );
}

function CustomerPanel({ t }) {
  const navigate = useNavigate();
  const c = t.contact;
  return (
    <Panel title="Customer" icon={UserRound} action={t.contactId && <Button size="small" onClick={() => navigate(`/helpdesk/customers/${t.contactId}`)}>360° view</Button>}>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <PersonAvatar name={t.requester?.name || t.requester?.email} size={36} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: TYPE.small, fontWeight: 700 }} noWrap>{t.requester?.name || (c ? `${c.firstName} ${c.lastName || ""}` : "Unknown")}</Typography>
          <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary }} noWrap>{t.requester?.email || "—"}</Typography>
          <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary }} noWrap>{t.requester?.phone || ""}</Typography>
          {c?.company?.name && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }} noWrap>{c.company.name}</Typography>}
        </Box>
      </Stack>
    </Panel>
  );
}

function AiPanel({ t, onChanged }) {
  const ctx = useHd();
  const [busy, setBusy] = useState("");
  const [summary, setSummary] = useState(null);
  const [kb, setKb] = useState(null);
  const enabled = ctx.settings?.ai?.enabled && ctx.can("ai.use");
  if (!enabled) return null;
  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } catch (err) {
      ctx.notify(errorMessage(err, "AI unavailable"), "warning");
    } finally {
      setBusy("");
    }
  };
  const ai = t.ai || {};
  return (
    <Panel title="AI copilot" icon={Sparkles}>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {ai.intent && <Chip size="small" label={`Intent: ${ai.intent}`} />}
        <SentimentChip sentiment={ai.sentiment} />
        {ai.language && <Chip size="small" label={ai.language} />}
      </Stack>
      {(summary?.summary || ai.summary) && <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary, mb: 1 }}>{summary?.summary || ai.summary}</Typography>}
      {summary?.nextBestAction && <Alert severity="info" icon={false} sx={{ py: 0, mb: 1, fontSize: TYPE.small }}><b>Next best action:</b> {summary.nextBestAction}{summary.followUp ? <><br /><b>Follow-up:</b> {summary.followUp}</> : null}</Alert>}
      {(ai.suggestedCategoryId || ai.suggestedPriorityId) && (
        <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, mb: 1 }}>
          Suggested: {ctx.categoryById.get(String(ai.suggestedCategoryId))?.name || "—"} · {ctx.priorityById.get(String(ai.suggestedPriorityId))?.name || "—"}
          {ai.confidence != null && ` (${Math.round(ai.confidence * 100)}%)`}
        </Typography>
      )}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button size="small" variant="outlined" disabled={Boolean(busy)} onClick={() => run("sum", async () => setSummary(await hd.aiSummary(t._id)))}>{busy === "sum" ? "…" : "Summarise"}</Button>
        <Button size="small" variant="outlined" disabled={Boolean(busy)} onClick={() => run("tri", async () => { await hd.aiTriage(t._id, { apply: false }); onChanged(); })}>{busy === "tri" ? "…" : "Suggest category"}</Button>
        {ai.suggestedCategoryId && ctx.can("tickets.update") && <Button size="small" variant="outlined" disabled={Boolean(busy)} onClick={() => run("apply", async () => { await hd.aiTriage(t._id, { apply: true }); onChanged(); ctx.notify("AI suggestions applied"); })}>Apply</Button>}
        <Button size="small" variant="outlined" disabled={Boolean(busy)} onClick={() => run("kb", async () => setKb(await hd.aiKb(t._id)))}>{busy === "kb" ? "…" : "Find articles"}</Button>
      </Stack>
      {kb && (
        <Box sx={{ mt: 1.25 }}>
          {!kb.length && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>No published article matches this ticket.</Typography>}
          {kb.map((a) => (
            <Box key={a._id} sx={{ p: 1, border: `1px solid ${T.border}`, borderRadius: "8px", mb: 0.75 }}>
              <Typography sx={{ fontSize: TYPE.small, fontWeight: 600 }}>{a.title}</Typography>
              <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}>{a.snippet}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Panel>
  );
}

function SlaPanel({ t }) {
  const s = t.sla || {};
  if (!s.policyId) return <Panel title="SLA"><Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>No SLA policy applies to this ticket.</Typography></Panel>;
  const row = (label, due, done, breached) => (
    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
      <Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>{label}</Typography>
      <Typography sx={{ fontSize: TYPE.small, fontWeight: 600, color: breached ? "#B42318" : done ? "#067647" : T.ink }}>
        {done ? `Met ${formatDateTime(done)}` : breached ? `Breached (${formatDateTime(due)})` : `Due ${formatDateTime(due)}`}
      </Typography>
    </Stack>
  );
  return (
    <Panel title="SLA">
      {row("First response", s.firstResponseDueAt, s.firstRespondedAt, s.firstResponseBreachedAt && !s.firstRespondedAt)}
      {row("Resolution", s.resolutionDueAt, t.resolvedAt && !s.resolutionBreachedAt ? t.resolvedAt : null, s.resolutionBreachedAt)}
      {s.pausedAt && <Chip size="small" label={`Paused since ${formatDateTime(s.pausedAt)}`} />}
    </Panel>
  );
}

function TasksPanel({ t, onChanged }) {
  const ctx = useHd();
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const add = async () => {
    if (!title.trim()) return;
    try {
      await hd.createTask(t._id, { title, dueAt: due || undefined, assigneeId: t.assignee?.id });
      setTitle("");
      setDue("");
      onChanged();
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
    }
  };
  return (
    <Panel title="Tasks">
      {(t.tasks || []).map((task) => (
        <FormControlLabel key={task._id} sx={{ display: "flex", mr: 0 }}
          control={<Checkbox size="small" checked={task.status === "done"} onChange={async (e) => { await hd.updateTask(task._id, { status: e.target.checked ? "done" : "open" }); onChanged(); }} />}
          label={<Typography sx={{ fontSize: TYPE.small, textDecoration: task.status === "done" ? "line-through" : "none" }}>{task.title}{task.dueAt ? ` · due ${formatDateTime(task.dueAt)}` : ""}</Typography>}
        />
      ))}
      {ctx.can("tickets.update") && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <TextField size="small" placeholder="Follow-up task" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth />
          <TextField size="small" type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} sx={{ width: 180 }} />
          <Button size="small" onClick={add}>Add</Button>
        </Stack>
      )}
    </Panel>
  );
}

function HistoryPanel({ id }) {
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState(0);
  useEffect(() => {
    hd.timeline(id).then(setRows).catch(() => setRows([]));
  }, [id]);
  return (
    <Panel title="History" icon={Lock}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 30, mb: 1, "& .MuiTab-root": { minHeight: 30, fontSize: TYPE.micro, textTransform: "none" } }}>
        <Tab label="Audit log" />
      </Tabs>
      {!rows ? <CircularProgress size={16} /> : rows.slice(0, 40).map((r) => (
        <Box key={r._id} sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: TYPE.micro, color: T.ink }}><b>{r.actor?.name || r.actor?.kind}</b> · {r.action.replace(/^ticket\.|^chat\./, "").replace(/_/g, " ")}</Typography>
          <Typography sx={{ fontSize: "0.66rem", color: T.inkMuted }}>{formatDateTime(r.createdAt)}</Typography>
        </Box>
      ))}
      <Divider sx={{ my: 1 }} />
      <Typography sx={{ fontSize: "0.66rem", color: T.inkMuted }}>Every change, AI action and email is recorded.</Typography>
    </Panel>
  );
}

function ResolveDialog({ t, onClose, onDone, required }) {
  const [summary, setSummary] = useState(t.resolution?.summary || "");
  const [notify, setNotify] = useState(true);
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Resolve {t.key}</DialogTitle>
      <DialogContent dividers>
        <TextField autoFocus label={`Resolution${required ? " (required)" : ""}`} fullWidth multiline minRows={4} value={summary} onChange={(e) => setSummary(e.target.value)}
          helperText="Sent to the customer in the resolution email ({{resolution}})." />
        <FormControlLabel control={<Checkbox checked={notify} onChange={(e) => setNotify(e.target.checked)} />} label="Email the customer and request feedback (CSAT)" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" sx={primaryButtonSx} disabled={required && !summary.trim()} onClick={() => onDone({ resolution: summary, notifyCustomer: notify })}>Resolve</Button>
      </DialogActions>
    </Dialog>
  );
}

function ReasonDialog({ title, label, onClose, onDone }) {
  const [v, setV] = useState("");
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers><TextField autoFocus fullWidth label={label} value={v} onChange={(e) => setV(e.target.value)} /></DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" sx={primaryButtonSx} disabled={!v.trim()} onClick={() => onDone(v.trim())}>Confirm</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function TicketDetail() {
  return (
    <HelpdeskPage>
      <Detail />
    </HelpdeskPage>
  );
}

/** "Aman is replying…" / "Also viewing: …" above the composer. */
const CollisionBar = ({ viewers }) => {
  if (!viewers.length) return null;
  const replying = viewers.filter((v) => v.replying);
  const names = (list) => list.map((v) => v.name).join(", ");
  return replying.length ? (
    <Alert severity="warning" sx={{ mx: 2, mt: 1.5, py: 0.25 }}>
      {names(replying)} {replying.length === 1 ? "is" : "are"} replying to this ticket right now — check before you send.
    </Alert>
  ) : (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ px: 2, pt: 1.25 }}>
      {viewers.slice(0, 5).map((v) => <Tooltip key={v.id} title={`${v.name} is viewing`}><span><PersonAvatar name={v.name} size={20} /></span></Tooltip>)}
      <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}>Also viewing: {names(viewers)}</Typography>
    </Stack>
  );
};
